// Pure function: the single source of truth for sale totals calculation.
// Used by the API to persist and by the web to preview ("Si paga con...").
// No I/O. No side effects. Same input always produces same output.

import type { SaleTotals, SaleTotalsInput } from "./types.js";

export function computeSaleTotals(input: SaleTotalsInput): SaleTotals {
  const lineDiscountTotalCents = input.items.reduce(
    (acc, it) => acc + it.lineDiscountCents,
    0
  );

  const itemsSubtotalCents = input.items.reduce(
    (acc, it) => acc + (it.qty * it.unitPriceCents - it.lineDiscountCents),
    0
  );

  const globalDiscountCents = Math.max(0, input.globalDiscountCents);
  const paymentDiscountEligibleCents = input.items.reduce(
    (acc, item) =>
      item.lineDiscountCents === 0
        ? acc + item.qty * item.unitPriceCents
        : acc,
    0
  );

  const paymentMethodDiscountCents =
    input.paymentMethodDiscountApplied &&
    globalDiscountCents === 0 &&
    input.paymentMethod.discountPct > 0
      ? Math.round(
          (paymentDiscountEligibleCents * input.paymentMethod.discountPct) / 100
        )
      : 0;

  const totalChargedCents = Math.max(
    0,
    itemsSubtotalCents - globalDiscountCents - paymentMethodDiscountCents
  );

  let netReceivedCents: number | null;
  if (input.paymentMethod.requiresNetReceived) {
    if (input.netReceivedOverride === undefined) {
      throw new Error("netReceived required for this payment method");
    }
    netReceivedCents = Math.max(0, input.netReceivedOverride);
  } else {
    netReceivedCents = totalChargedCents;
  }

  const commissionCents =
    netReceivedCents === null
      ? 0
      : Math.max(0, totalChargedCents - netReceivedCents);

  return {
    itemsSubtotalCents,
    lineDiscountTotalCents,
    globalDiscountCents,
    paymentMethodDiscountCents,
    totalChargedCents,
    netReceivedCents,
    commissionCents,
  };
}
